// src/utils/axios.ts
import axios from "axios";
import { jwtDecode } from "jwt-decode";
var apiBaseUrl = "";
var streamingBaseUrl = "";
var axiosInstance = null;
var WORKSPACE_STORAGE_KEY = "coremind:workspace-id";
var getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};
var isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1e3 < Date.now();
  } catch (error) {
    console.error("[camera] Error decoding token:", error);
    return true;
  }
};
var setApiBaseUrl = (url) => {
  apiBaseUrl = url;
  axiosInstance = createAxiosInstanceForBase(apiBaseUrl);
};
var setStreamingBaseUrl = (url) => {
  streamingBaseUrl = url;
};
var getStreamingBaseUrl = () => streamingBaseUrl || apiBaseUrl;
var createAxiosInstanceForBase = (baseUrl) => {
  const instance = axios.create({
    baseURL: baseUrl,
    headers: {
      "Content-Type": "application/json"
    },
    withCredentials: true
  });
  instance.interceptors.request.use((config) => {
    const base = config.baseURL ?? baseUrl;
    if (typeof base === "string" && /\/api\/v1\/?$/.test(base) && typeof config.url === "string" && config.url.startsWith("/api/v1/")) {
      config.url = config.url.replace(/^\/api\/v1/, "");
    }
    const token = getCookie("idToken");
    if (token && !isTokenExpired(token)) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (typeof window !== "undefined") {
      const workspaceId = window.localStorage.getItem(WORKSPACE_STORAGE_KEY);
      if (workspaceId) {
        config.headers["X-Tenant-Id"] = workspaceId;
      }
      const siteHostname = window.__COREMIND_SITE_HOSTNAME__;
      if (typeof siteHostname === "string" && siteHostname.trim()) {
        config.headers["X-Coremind-Site-Host"] = siteHostname.trim();
      }
      const userId = window.__USER_ID__;
      if (typeof userId === "string" && userId.trim()) {
        config.headers["X-User-Id"] = userId.trim();
      }
    }
    return config;
  });
  return instance;
};
var getAxios = () => {
  if (!axiosInstance) {
    const windowApi = typeof window !== "undefined" ? window.__COREMIND_API_URL__ : "";
    if (windowApi) {
      setApiBaseUrl(windowApi);
    }
  }
  if (!axiosInstance) {
    throw new Error("Axios instance not initialized. Call setApiBaseUrl() first.");
  }
  return axiosInstance;
};
var getStreamingAxios = () => {
  const baseUrl = getStreamingBaseUrl();
  if (!baseUrl) {
    throw new Error("Streaming base URL is not configured.");
  }
  return createAxiosInstanceForBase(baseUrl);
};

// src/hooks/useCameraRecorder.ts
import { useState, useEffect, useCallback, useRef } from "react";
var useCameraRecorder = () => {
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null);
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [ready, setReady] = useState(false);
  const [videoResolution, setVideoResolution] = useState({
    width: 1280,
    height: 720
  });
  const startCamera = useCallback(async () => {
    try {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (!navigator.mediaDevices?.getUserMedia) {
        alert("Your browser does not support camera access.");
        return;
      }
      if (isSafari) {
        console.warn("[camera] Safari detected. Ensure camera access is granted in browser settings.");
      }
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: videoResolution.width },
          height: { ideal: videoResolution.height }
        },
        audio: true
      });
      streamRef.current = newStream;
      setStream(newStream);
      setReady(true);
    } catch (error) {
      const err = error;
      console.error("[camera] Camera access error:", err);
      if (err.name === "NotAllowedError") {
        alert("Camera access is blocked. Please enable it in browser settings.");
      } else if (err.name === "NotReadableError") {
        alert("Camera is in use by another app.");
      } else {
        alert(`Failed to start camera: ${err.message}`);
      }
      setReady(false);
    }
  }, [facingMode, videoResolution.height, videoResolution.width]);
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStream(null);
    setReady(false);
  }, []);
  const toggleCamera = useCallback(async () => {
    stopCamera();
    await new Promise((res) => setTimeout(res, 200));
    setFacingMode((prev) => prev === "user" ? "environment" : "user");
  }, [stopCamera]);
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);
  const startRecording = useCallback(() => {
    setIsRecording(true);
  }, []);
  const stopRecording = useCallback(() => {
    setIsRecording(false);
  }, []);
  return {
    stream,
    capturedMedia,
    isRecording,
    isProcessing,
    facingMode,
    ready,
    startCamera,
    stopCamera,
    toggleCamera,
    startRecording,
    stopRecording,
    setCapturedMedia,
    setIsProcessing,
    setFacingMode,
    videoResolution,
    setVideoResolution
  };
};

// src/hooks/useCameraControls.ts
import { useEffect as useEffect2 } from "react";
var useCameraControls = ({
  stream,
  isRecording,
  useMp4 = true,
  stopRecording,
  setCapturedMedia,
  setIsProcessing,
  invokeRecorder
}) => {
  useEffect2(() => {
    if (stream) {
      invokeRecorder({
        stream,
        isRecording,
        stopRecording,
        setCapturedMedia,
        setIsProcessing,
        useMp4
      });
    }
  }, [stream, isRecording]);
};

// src/hooks/useCameraStreamer.ts
import { useEffect as useEffect3, useRef as useRef2, useState as useState2 } from "react";
import jsQR from "jsqr";
import { v4 as uuidv4 } from "uuid";
var buildStreamingUrl = (path) => {
  const base = getStreamingBaseUrl();
  if (!base) {
    throw new Error("Streaming base URL is not configured.");
  }
  return new URL(path, base);
};
var toWebSocketUrl = (url) => {
  const wsUrl = new URL(url.toString());
  wsUrl.protocol = wsUrl.protocol === "https:" ? "wss:" : "ws:";
  return wsUrl.toString();
};
var useCameraStreamer = ({
  parentFolderId,
  subFolderId,
  userId,
  qrEnabled,
  jpegInterval = 1e3,
  useJpegMode = true,
  jpegQuality = 0.7,
  maxWidth = 1280,
  qrResetThreshold = 5,
  restartOnQrReset = true,
  onQrDetected,
  onStop,
  onQrSessionEnd,
  onFrame
}) => {
  const videoRef = useRef2(null);
  const canvasRef = useRef2(document.createElement("canvas"));
  const uploadCanvasRef = useRef2(document.createElement("canvas"));
  const socketRef = useRef2(null);
  const intervalRef = useRef2(null);
  const [isStreaming, setIsStreaming] = useState2(false);
  const [shouldSendImages, setShouldSendImages] = useState2(false);
  const [qrClearCount, setQrClearCount] = useState2(0);
  const [currentSubFolderId, setCurrentSubFolderId] = useState2(subFolderId);
  const qrStillCount = useRef2(0);
  const qrLocked = useRef2(false);
  const qrLastSeen = useRef2(null);
  const hasSeenQr = useRef2(false);
  const pendingSubFolderId = useRef2(null);
  const start = async (newSubFolderId = currentSubFolderId, allowSendAfterStart = false) => {
    const wsUrl = buildStreamingUrl("/api/v1/streaming/ws/stream");
    wsUrl.searchParams.set("parent_folder", parentFolderId);
    wsUrl.searchParams.set("sub_folder", newSubFolderId);
    const ws = new WebSocket(toWebSocketUrl(wsUrl));
    socketRef.current = ws;
    setCurrentSubFolderId(newSubFolderId);
    ws.onerror = (e) => console.error("[camera] WebSocket error:", e);
    ws.onclose = () => console.warn("[camera] WebSocket connection closed");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: !useJpegMode
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("[camera] Camera access error:", err);
      return;
    }
    const context = canvasRef.current.getContext("2d", { willReadFrequently: true });
    intervalRef.current = window.setInterval(() => {
      if (!videoRef.current || !context) return;
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      const imageData = context.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
      const qr = jsQR(imageData.data, imageData.width, imageData.height);
      const qrData = qr?.data?.trim() ?? "";
      const qrVisible = qrData.length > 0;
      let allowSend = true;
      if (qrEnabled) {
        if (qrVisible) {
          if (!qrLocked.current) {
            qrLocked.current = true;
            setShouldSendImages(false);
            qrLastSeen.current = qrData;
            if (onQrDetected) {
              void (async () => {
                try {
                  const nextId = await onQrDetected(qrData);
                  pendingSubFolderId.current = nextId ?? null;
                  hasSeenQr.current = Boolean(nextId);
                } catch (err) {
                  console.error("[camera] onQrDetected failed:", err);
                  pendingSubFolderId.current = null;
                  hasSeenQr.current = false;
                }
              })();
            }
          }
          qrStillCount.current = 0;
          setQrClearCount(0);
          allowSend = false;
        } else {
          qrStillCount.current += 1;
          setQrClearCount(qrStillCount.current);
          if (qrLocked.current && qrStillCount.current >= qrResetThreshold) {
            qrLocked.current = false;
            setShouldSendImages(true);
            if (qrLastSeen.current) {
              onQrSessionEnd?.(qrLastSeen.current);
              qrLastSeen.current = null;
            }
            if (restartOnQrReset) {
              const nextSubFolderId = pendingSubFolderId.current || (!onQrDetected ? uuidv4() : null);
              pendingSubFolderId.current = null;
              if (nextSubFolderId) {
                restartStreamWithNewFolder(nextSubFolderId);
              } else {
                setShouldSendImages(false);
                hasSeenQr.current = false;
              }
            }
          }
          allowSend = !qrLocked.current;
        }
      } else {
        allowSend = true;
        setShouldSendImages(true);
      }
      if (onQrDetected && !hasSeenQr.current && !qrVisible) {
        allowSend = false;
        setShouldSendImages(false);
      }
      if (useJpegMode && allowSend && socketRef.current?.readyState === WebSocket.OPEN) {
        const sourceCanvas = canvasRef.current;
        const uploadCanvas = uploadCanvasRef.current;
        const sourceWidth = sourceCanvas.width;
        const sourceHeight = sourceCanvas.height;
        if (!sourceWidth || !sourceHeight) return;
        const scale = maxWidth && sourceWidth > maxWidth ? maxWidth / sourceWidth : 1;
        const targetWidth = Math.round(sourceWidth * scale);
        const targetHeight = Math.round(sourceHeight * scale);
        uploadCanvas.width = targetWidth;
        uploadCanvas.height = targetHeight;
        const uploadContext = uploadCanvas.getContext("2d");
        if (!uploadContext) return;
        uploadContext.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);
        uploadCanvas.toBlob(
          (blob) => {
            if (blob) {
              onFrame?.(blob);
              blob.arrayBuffer().then((buf) => {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                  socketRef.current.send(buf);
                }
              });
            }
          },
          "image/jpeg",
          jpegQuality
        );
      }
    }, jpegInterval);
    const allowImmediateSend = !qrEnabled || !onQrDetected || allowSendAfterStart;
    setIsStreaming(true);
    setShouldSendImages(allowImmediateSend);
    hasSeenQr.current = allowImmediateSend;
  };
  const stopInProgress = useRef2(false);
  const stop = async () => {
    if (stopInProgress.current) return;
    stopInProgress.current = true;
    try {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const mediaStream = videoRef.current?.srcObject;
      mediaStream?.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setIsStreaming(false);
      setShouldSendImages(false);
      setQrClearCount(0);
      qrStillCount.current = 0;
      qrLocked.current = false;
      qrLastSeen.current = null;
      hasSeenQr.current = false;
      pendingSubFolderId.current = null;
    } finally {
      stopInProgress.current = false;
      onStop?.();
    }
  };
  const finalize = async () => {
    try {
      await getStreamingAxios().post("/api/v1/streaming/finalize-parent", {
        parent_folder: parentFolderId,
        user_id: userId
      });
    } catch (err) {
      console.error("[camera] Finalize error:", err);
    }
  };
  const restartStreamWithNewFolder = async (newSubFolderId = uuidv4()) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    await start(newSubFolderId, true);
  };
  useEffect3(() => {
    return () => {
      if (isStreaming) stop();
    };
  }, [isStreaming]);
  return {
    videoRef,
    start,
    stop,
    finalize,
    isStreaming,
    shouldSendImages,
    qrStillCount: qrStillCount.current,
    currentSubFolderId
  };
};

// src/hooks/useVideoRecorderRaw.ts
import { useEffect as useEffect4, useRef as useRef3 } from "react";
var useVideoRecorderRaw = ({
  stream,
  isRecording,
  stopRecording,
  setCapturedMedia,
  setIsProcessing,
  maxDurationMs
}) => {
  const mediaRecorderRef = useRef3(null);
  const chunksRef = useRef3([]);
  const startTimeRef = useRef3(null);
  const timeoutRef = useRef3(null);
  useEffect4(() => {
    if (!stream) return;
    const startRecording = () => {
      if (!stream.getTracks().length) {
        console.warn("[camera] Raw: No media tracks available \u2014 cannot record.");
        return;
      }
      if (mediaRecorderRef.current?.state === "recording") {
        console.warn("[camera] Raw: Already recording. Ignoring.");
        return;
      }
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.onstart = () => {
        startTimeRef.current = Date.now();
        if (maxDurationMs) {
          timeoutRef.current = setTimeout(() => {
            recorder.stop();
          }, maxDurationMs);
        }
      };
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (chunksRef.current.length === 0) {
          stopRecording();
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setIsProcessing(true);
        setCapturedMedia(blob);
        setIsProcessing(false);
        stopRecording();
        chunksRef.current = [];
      };
      recorder.onerror = (event) => {
        console.error("[camera] Raw: MediaRecorder error:", event.error);
        stopRecording();
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
    };
    if (isRecording) {
      startRecording();
    } else if (mediaRecorderRef.current?.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.warn("[camera] Raw: Failed to stop recorder:", err);
      }
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isRecording, stream, maxDurationMs, setCapturedMedia, setIsProcessing, stopRecording]);
  useEffect4(() => {
    if (!isRecording && mediaRecorderRef.current?.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.warn("[camera] Raw observer stop failed:", err);
      }
    }
  }, [isRecording]);
};

// src/hooks/useVideoRecorderWithFFmpeg.ts
import { useEffect as useEffect5, useRef as useRef4 } from "react";

// src/hooks/useFFmpeg.ts
var useFFmpeg = () => {
  const load = async () => {
    console.log("[camera] FFmpeg skipped \u2014 using raw WebM.");
  };
  const convertWebMtoMP4 = async (webmBlob) => {
    return webmBlob;
  };
  return { load, convertWebMtoMP4, loaded: true };
};
var useFFmpeg_default = useFFmpeg;

// src/hooks/useVideoRecorderWithFFmpeg.ts
var useVideoRecorderWithFFmpeg = ({
  stream,
  isRecording,
  stopRecording,
  setCapturedMedia,
  setIsProcessing,
  maxDurationMs
}) => {
  const mediaRecorderRef = useRef4(null);
  const chunksRef = useRef4([]);
  const timeoutRef = useRef4(null);
  const startPendingRef = useRef4(false);
  const { load, convertWebMtoMP4, loaded } = useFFmpeg_default();
  const withTimeout = (promise, ms) => {
    return Promise.race([
      promise,
      new Promise(
        (_, reject) => setTimeout(() => reject(new Error("FFmpeg conversion timed out")), ms)
      )
    ]);
  };
  useEffect5(() => {
    if (!stream) return;
    const startRecording = async () => {
      if (startPendingRef.current) {
        return;
      }
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state === "recording") {
          return;
        } else {
          mediaRecorderRef.current = null;
        }
      }
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      if (!stream.getTracks().length) {
        return;
      }
      chunksRef.current = [];
      startPendingRef.current = true;
      try {
        const recorder = new MediaRecorder(stream, { mimeType });
        recorder.onstart = () => {
          if (maxDurationMs) {
            timeoutRef.current = setTimeout(() => {
              recorder.stop();
            }, maxDurationMs);
          }
        };
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        recorder.onerror = (event) => {
          console.error("[camera] MediaRecorder error:", event.error);
        };
        recorder.onstop = async () => {
          startPendingRef.current = false;
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          mediaRecorderRef.current = null;
          if (chunksRef.current.length === 0) {
            stopRecording();
            return;
          }
          const webmBlob = new Blob(chunksRef.current, { type: mimeType });
          setIsProcessing(true);
          try {
            if (!loaded) {
              await load();
            }
            const mp4Blob = await withTimeout(convertWebMtoMP4(webmBlob), 2e4);
            setCapturedMedia(mp4Blob);
          } catch (error) {
            console.error("[camera] Conversion failed, using original WebM", error);
            setCapturedMedia(webmBlob);
          } finally {
            setIsProcessing(false);
            stopRecording();
            chunksRef.current = [];
          }
        };
        mediaRecorderRef.current = recorder;
        recorder.start();
      } catch (error) {
        console.error("[camera] Failed to initialize MediaRecorder", error);
        startPendingRef.current = false;
        stopRecording();
      }
    };
    if (isRecording) {
      startRecording();
    } else if (mediaRecorderRef.current?.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.warn("[camera] Error while stopping recorder:", err);
        mediaRecorderRef.current = null;
        stopRecording();
      }
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isRecording, stream, maxDurationMs, setCapturedMedia, setIsProcessing, stopRecording, load, loaded, convertWebMtoMP4]);
  useEffect5(() => {
    if (!isRecording && mediaRecorderRef.current?.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.warn("[camera] Observer stop failed:", err);
        mediaRecorderRef.current = null;
      }
    }
  }, [isRecording]);
};

// src/hooks/useVideoUpload.ts
import { useState as useState3 } from "react";
function useVideoUpload({
  userEmail,
  tenantId,
  userId,
  binName,
  itemName,
  itemId,
  onUploaded,
  setCapturedMedia,
  setIsProcessing
}) {
  const [uploading, setUploading] = useState3(false);
  const uploadMedia = async (capturedMedia, overrides) => {
    if (!capturedMedia) {
      console.warn("[camera] No media to upload");
      return;
    }
    setUploading(true);
    try {
      const resolved = {
        userEmail,
        tenantId,
        userId,
        binName,
        itemName,
        itemId,
        ...overrides
      };
      const formData = new FormData();
      const filename = `video_${resolved.userEmail}_${Date.now()}.mp4`;
      formData.append("file", capturedMedia, filename);
      formData.append("email", resolved.userEmail);
      if (resolved.tenantId) formData.append("tenant_id", resolved.tenantId);
      if (resolved.userId) formData.append("user_id", resolved.userId);
      if (resolved.binName) formData.append("bin_name", resolved.binName);
      if (resolved.itemName) formData.append("item_name", resolved.itemName);
      if (resolved.itemId) formData.append("item_id", resolved.itemId);
      const res = await getAxios().post("/api/v1/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (onUploaded) {
        onUploaded(res.data);
      }
      setCapturedMedia(null);
      setIsProcessing(false);
    } catch (error) {
      console.error("[camera] Upload failed", error);
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };
  return { uploadMedia, uploading };
}
export {
  getAxios,
  getStreamingAxios,
  setApiBaseUrl,
  setStreamingBaseUrl,
  useCameraControls,
  useCameraRecorder,
  useCameraStreamer,
  useVideoRecorderRaw,
  useVideoRecorderWithFFmpeg,
  useVideoUpload
};
